'use client';

import { 
  CalculatorId, 
  CalculatorType,
  ControlIds,
  type CalculatorConfig
} from '../../../lib/calculators/calculator-config';
import { TableDataJSON } from '../../../src/types/table-json';
import { LazyTabLabECharts } from '@repo/ui';
import { 
  lineGraphControls, 
  getLineGraphEChartsConfig, 
  type LineGraphControlValues 
} from '../line-graph/line-graph.config';

export const areaGraphConfig: CalculatorConfig<CalculatorId.AreaChart> = {
  id: CalculatorId.AreaChart,
  type: CalculatorType.Graph,
  name: 'Area Graph',
  initialData: TableDataJSON.from({
    columns: [
      { 
        id: 'month', 
        name: 'Month', 
        values: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] 
      },
      { 
        id: 'revenue', 
        name: 'Revenue ($K)', 
        values: ['50', '55', '65', '80', '90', '100', '110', '115', '105', '95', '85', '75'] 
      },
      {
        id: 'profit',
        name: 'Profit ($K)',
        values: ['10', '12', '15', '20', '25', '30', '35', '38', '32', '28', '22', '18']
      }
    ]
  }),
  initialControlValues: {
    [ControlIds.XColumn]: 'month',
    [ControlIds.Values]: ['revenue', 'profit'],
    [ControlIds.Title]: 'Monthly Revenue and Profit',
    [ControlIds.ShowArea]: 'true',
    [ControlIds.AreaOpacity]: '0.3',
    [ControlIds.MarkerSize]: '4'
  },
  controls: lineGraphControls,
  getInitialConfig: (data: TableDataJSON) => {
    const numericColumns = data.columns.filter(col => 
      col.values.every(val => !isNaN(Number(val)))
    );
    const nonNumericColumns = data.columns.filter(col => 
      !col.values.every(val => !isNaN(Number(val)))
    );

    return {
      [ControlIds.XColumn]: nonNumericColumns[0]?.id || data.columns[0]?.id,
      [ControlIds.Values]: numericColumns.map(col => col.id),
      [ControlIds.Title]: 'Area Graph',
      [ControlIds.ShowArea]: 'true',
      [ControlIds.AreaOpacity]: '0.3',
      [ControlIds.MarkerSize]: '4'
    };
  },
  getVisualizationData: ({ data, controls }) => {
    const areaGraphControls = controls as LineGraphControlValues;
    
    if (!data?.columns?.length || !areaGraphControls[ControlIds.Values]?.length) {
      return null;
    }

    const xColumn = data.columns.find(col => col.id === areaGraphControls[ControlIds.XColumn]);
    const selectedColumns = areaGraphControls[ControlIds.Values]
      .map(id => data.columns.find(col => col.id === id))
      .filter((col): col is typeof data.columns[0] => col !== undefined);

    if (!xColumn || !selectedColumns.length) {
      return null;
    }

    const series = selectedColumns.map(col => ({
      name: col.name,
      data: col.values.map(v => Number(v))
    }));

    return getLineGraphEChartsConfig({
      xData: xColumn.values,
      series,
      title: areaGraphControls[ControlIds.Title],
      showArea: areaGraphControls[ControlIds.ShowArea] === 'true',
      areaOpacity: Number(areaGraphControls[ControlIds.AreaOpacity]),
      markerSize: Number(areaGraphControls[ControlIds.MarkerSize])
    });
  },
  render: ({ visualizationData }) => 
    visualizationData ? (
      <LazyTabLabECharts option={visualizationData} className="h-full w-full" />
    ) : null
}; 